/*
 * ANIME CRUSH — GUIDE INDEX SEARCH AND FILTER
 *
 * One grid, one search box, two independent filter groups. §6 asks for search
 * and filtering; this is the smallest thing that delivers it honestly.
 *
 * TWO GROUPS, NOT ONE ROW OF CHIPS. A facet ("with merchandise") and a decade
 * are different questions, so they combine instead of replacing each other:
 * "1990s" AND "has trailer" is a reasonable thing to ask and a single chip row
 * cannot express it. Within a group the chips are exclusive, which is why each
 * group carries role="group" in the markup and the chips carry aria-pressed.
 *
 * PROGRESSIVE. The controls are rendered `hidden` and only revealed once this
 * script has bound them, so a reader without JavaScript is never shown a search
 * box that does nothing. Every card stays in the DOM either way - filtering
 * hides, it never removes - so the index remains fully crawlable and every
 * anime keeps its internal link.
 *
 * SEARCH MATCHES MORE THAN THE TITLE. data-ac-terms carries the title, the
 * studio and the genres, because "Ghibli" and "cyberpunk" are things a reader
 * will type and neither is a title.
 *
 * NO PAGINATION, DELIBERATELY. §6 warns against rendering thousands of records
 * in one page. This is 111 cards: roughly the weight of one collection page of
 * products, and paginating it would put a barrier between a reader and the thing
 * they came to browse. Past a few hundred this needs revisiting, and the grid is
 * the measurable part.
 */
(function () {
  'use strict';

  function init() {
    var tools = document.querySelector('[data-ac-guide-tools]');
    var grid = document.querySelector('[data-ac-guide-grid]');
    if (!tools || !grid) return;

    var cards = Array.prototype.slice.call(grid.querySelectorAll('[data-ac-card]'));
    if (!cards.length) return;

    var search = tools.querySelector('[data-ac-guide-search]');
    var facetChips = Array.prototype.slice.call(tools.querySelectorAll('[data-ac-guide-filter]'));
    var decadeChips = Array.prototype.slice.call(tools.querySelectorAll('[data-ac-guide-decade]'));
    var result = tools.querySelector('[data-ac-guide-result]');
    var empty = document.querySelector('[data-ac-guide-empty]');

    var genreSel = tools.querySelector('[data-ac-guide-genre]');
    var formatSel = tools.querySelector('[data-ac-guide-format]');
    var resetBtn = tools.querySelector('[data-ac-guide-reset]');

    var facet = 'all';
    var decade = 'all';
    var genre = 'all';
    var format = 'all';
    var term = '';

    var FACET_ATTR = {
      shop: 'data-ac-has-shop',
      watch: 'data-ac-has-watch',
      trailer: 'data-ac-has-trailer',
      airing: 'data-ac-airing'
    };

    function matches(card) {
      if (facet !== 'all') {
        var attr = FACET_ATTR[facet];
        if (!attr || card.getAttribute(attr) !== '1') return false;
      }
      if (decade !== 'all' && card.getAttribute('data-ac-decade') !== decade) return false;
      /* EVERY GROUP IS AND-ed. The brief asks that filters work together, so a
         genre and a decade narrow each other rather than replacing each other. */
      if (format !== 'all' && card.getAttribute('data-ac-format') !== format) return false;
      if (genre !== 'all') {
        /* data-ac-genres is SPACE-joined - "action adventure drama" - and two
           genres contain a space themselves ("slice of life", "mahou shoujo").
           So the probe is space-BOUNDED rather than split on a delimiter: it
           matches a one-word genre and a three-word one with the same rule, and
           still cannot match a fragment, because both ends must be a boundary. */
        var gl = ' ' + (card.getAttribute('data-ac-genres') || '') + ' ';
        if (gl.indexOf(' ' + genre + ' ') < 0) return false;
      }
      if (!term) return true;
      var hay = card.getAttribute('data-ac-terms') || card.getAttribute('data-ac-name') || '';
      return hay.indexOf(term) >= 0;
    }

    function apply() {
      var shown = 0;
      cards.forEach(function (c) {
        var on = matches(c);
        /* hidden, not removed: the link stays in the document. */
        c.hidden = !on;
        if (on) shown++;
      });
      if (empty) empty.hidden = shown !== 0;
      if (result) {
        result.textContent = shown === cards.length
          ? cards.length + ' anime'
          : shown + ' of ' + cards.length + ' anime';
      }
    }

    if (search) {
      var t = null;
      search.addEventListener('input', function () {
        window.clearTimeout(t);
        /* A short debounce: typing "berserk" should not run seven passes. */
        t = window.setTimeout(function () {
          term = search.value.trim().toLowerCase();
          apply();
        }, 120);
      });
    }

    /* One binder for both groups: within a group the chips are exclusive, and
       the groups are independent of each other. */
    function bindGroup(chips, attr, set) {
      chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          set(chip.getAttribute(attr) || 'all');
          chips.forEach(function (c) {
            var on = c === chip;
            c.classList.toggle('is-on', on);
            c.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
          apply();
        });
        chip.setAttribute('aria-pressed', chip.classList.contains('is-on') ? 'true' : 'false');
      });
    }

    /* A COLLAPSED DISCLOSURE MUST NOT HIDE AN ACTIVE FILTER.
     *
     * "Has trailer" now lives inside <details>. Because the facet group is
     * single-select, a reader could select it, collapse the disclosure, and be
     * left looking at a filtered list with no visible control explaining why.
     * So the disclosure is forced open whenever the filter inside it is the
     * active one, and it is never force-closed - collapsing it is still the
     * reader's choice, it just cannot strand them. */
    var moreBox = tools.querySelector('[data-ac-guide-more]');
    function syncMore() {
      if (!moreBox) return;
      /* A select left on a real value is just as invisible as a chip left on
         when the disclosure is shut, so both count as "active inside". */
      var activeInside = moreBox.querySelector('[data-ac-guide-filter].is-on')
        || (genreSel && genreSel.value !== 'all')
        || (formatSel && formatSel.value !== 'all');
      if (activeInside) moreBox.open = true;
    }

    bindGroup(facetChips, 'data-ac-guide-filter', function (v) { facet = v; });
    bindGroup(decadeChips, 'data-ac-guide-decade', function (v) { decade = v; });

    if (genreSel) {
      genreSel.addEventListener('change', function () { genre = genreSel.value; apply(); });
    }
    if (formatSel) {
      formatSel.addEventListener('change', function () { format = formatSel.value; apply(); });
    }

    /* Reset clears EVERY group, including the ones inside the disclosure, and
       puts the chips back to their own default rather than just clearing state -
       a control that leaves the UI showing a filter it is no longer applying is
       worse than no control. */
    if (resetBtn) {
      resetBtn.addEventListener('click', function () {
        facet = 'all'; decade = 'all'; genre = 'all'; format = 'all'; term = '';
        if (search) search.value = '';
        if (genreSel) genreSel.value = 'all';
        if (formatSel) formatSel.value = 'all';
        facetChips.forEach(function (c) {
          c.classList.toggle('is-on', c.getAttribute('data-ac-guide-filter') === 'all');
        });
        decadeChips.forEach(function (c) {
          c.classList.toggle('is-on', c.getAttribute('data-ac-guide-decade') === 'all');
        });
        apply();
      });
    }

    /* after every facet change, not only on load */
    facetChips.forEach(function (c) { c.addEventListener('click', syncMore); });

    tools.hidden = false;
    syncMore();
    apply();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
